import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { graphql } from '@/gql'
import { gqlClient } from '@/lib/graphql'

/**
 * All lounge server state: the lounges themselves, their members, their
 * invites, and every mutation that touches them.
 */

export const loungeKeys = {
  all: ['lounges'] as const,
  mine: () => [...loungeKeys.all, 'mine'] as const,
  detail: (slug: string) => [...loungeKeys.all, 'detail', slug] as const,
  members: (loungeId: string) => [...loungeKeys.all, 'members', loungeId] as const,
  invites: (loungeId: string) => [...loungeKeys.all, 'invites', loungeId] as const,
}

const MyLoungesQuery = graphql(`
  query MyLounges {
    myLounges {
      id
      slug
      name
      myRole
      memberCount
      unreadCount
      mentionCount
    }
  }
`)

const LoungeQuery = graphql(`
  query Lounge($slug: String!) {
    lounge(slug: $slug) {
      id
      slug
      name
      description
      ownerId
      myRole
      memberCount
      createdAt
    }
  }
`)

const LoungeMembersQuery = graphql(`
  query LoungeMembers($loungeId: UUID!) {
    loungeMembers(loungeId: $loungeId) {
      role
      joinedAt
      presence
      user {
        id
        handle
        displayName
        avatarUrl
      }
    }
  }
`)

const LoungeInvitesQuery = graphql(`
  query LoungeInvites($loungeId: UUID!) {
    loungeInvites(loungeId: $loungeId) {
      id
      code
      uses
      maxUses
      expiresAt
      revokedAt
      isValid
      createdAt
    }
  }
`)

const CreateLoungeMutation = graphql(`
  mutation CreateLounge($name: String!) {
    createLounge(name: $name) {
      id
      slug
      name
      myRole
      memberCount
    }
  }
`)

const RenameLoungeMutation = graphql(`
  mutation RenameLounge($loungeId: UUID!, $name: String!) {
    renameLounge(loungeId: $loungeId, name: $name) {
      id
      slug
      name
    }
  }
`)

const DeleteLoungeMutation = graphql(`
  mutation DeleteLounge($loungeId: UUID!) {
    deleteLounge(loungeId: $loungeId)
  }
`)

const CreateInviteMutation = graphql(`
  mutation CreateInvite($loungeId: UUID!, $maxUses: Int, $expiresAt: DateTime) {
    createInvite(loungeId: $loungeId, maxUses: $maxUses, expiresAt: $expiresAt) {
      id
      code
      uses
      maxUses
      expiresAt
      revokedAt
      isValid
      createdAt
    }
  }
`)

const RevokeInviteMutation = graphql(`
  mutation RevokeInvite($code: String!) {
    revokeInvite(code: $code) {
      id
      code
      isValid
      revokedAt
    }
  }
`)

const JoinLoungeMutation = graphql(`
  mutation JoinLounge($code: String!) {
    joinLounge(code: $code) {
      id
      slug
      name
      myRole
      memberCount
    }
  }
`)

const KickMemberMutation = graphql(`
  mutation KickMember($loungeId: UUID!, $userId: UUID!) {
    kickMember(loungeId: $loungeId, userId: $userId)
  }
`)

const BanMemberMutation = graphql(`
  mutation BanMember($loungeId: UUID!, $userId: UUID!, $reason: String) {
    banMember(loungeId: $loungeId, userId: $userId, reason: $reason)
  }
`)

const UnbanMemberMutation = graphql(`
  mutation UnbanMember($loungeId: UUID!, $userId: UUID!) {
    unbanMember(loungeId: $loungeId, userId: $userId)
  }
`)

export const myLoungesQueryOptions = () =>
  queryOptions({
    queryKey: loungeKeys.mine(),
    queryFn: () => gqlClient.request(MyLoungesQuery),
    staleTime: 30_000,
  })

export const loungeQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: loungeKeys.detail(slug),
    queryFn: () => gqlClient.request(LoungeQuery, { slug }),
    staleTime: 30_000,
  })

export const loungeMembersQueryOptions = (loungeId: string) =>
  queryOptions({
    queryKey: loungeKeys.members(loungeId),
    queryFn: () => gqlClient.request(LoungeMembersQuery, { loungeId }),
  })

export const loungeInvitesQueryOptions = (loungeId: string) =>
  queryOptions({
    queryKey: loungeKeys.invites(loungeId),
    queryFn: () => gqlClient.request(LoungeInvitesQuery, { loungeId }),
  })

export function useMyLounges() {
  return useQuery(myLoungesQueryOptions())
}

export function useLounge(slug: string) {
  return useQuery(loungeQueryOptions(slug))
}

export function useLoungeMembers(loungeId: string) {
  return useQuery(loungeMembersQueryOptions(loungeId))
}

export function useLoungeInvites(loungeId: string) {
  return useQuery(loungeInvitesQueryOptions(loungeId))
}

export function useCreateLounge() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => gqlClient.request(CreateLoungeMutation, { name }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: loungeKeys.mine() })
    },
  })
}

export function useRenameLounge() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { loungeId: string; name: string }) =>
      gqlClient.request(RenameLoungeMutation, input),
    onSuccess: async () => {
      // The whole subtree: the rail shows names and the header shows one too.
      await client.invalidateQueries({ queryKey: loungeKeys.all })
    },
  })
}

export function useDeleteLounge() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (loungeId: string) => gqlClient.request(DeleteLoungeMutation, { loungeId }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: loungeKeys.all })
    },
  })
}

export function useCreateInvite(loungeId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { maxUses?: number | null; expiresAt?: string | null }) =>
      gqlClient.request(CreateInviteMutation, { loungeId, ...input }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: loungeKeys.invites(loungeId) })
    },
  })
}

export function useRevokeInvite(loungeId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (code: string) => gqlClient.request(RevokeInviteMutation, { code }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: loungeKeys.invites(loungeId) })
    },
  })
}

export function useJoinLounge() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (code: string) => gqlClient.request(JoinLoungeMutation, { code }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: loungeKeys.all })
    },
  })
}

export function useKickMember(loungeId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => gqlClient.request(KickMemberMutation, { loungeId, userId }),
    onSuccess: async () => {
      // Members *and* the lounge itself — memberCount is on the lounge.
      await client.invalidateQueries({ queryKey: loungeKeys.all })
    },
  })
}

/**
 * Ban, with the reason the API always accepted and the UI never sent.
 *
 * The reason outlives the ban row: lifting a ban deletes that row, and the
 * audit log keeps the record of why it was made.
 */
export function useBanMember(loungeId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string | null }) =>
      gqlClient.request(BanMemberMutation, {
        loungeId,
        userId,
        reason: reason?.trim() ? reason.trim() : null,
      }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: loungeKeys.all })
    },
  })
}

/** Lift a ban. Does not re-add them — they have to be invited back. */
export function useUnbanMember(loungeId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => gqlClient.request(UnbanMemberMutation, { loungeId, userId }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: loungeKeys.all })
    },
  })
}
